import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, Mail, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import '../styles/Login.css';

const CALLBACK_URL = `${window.location.origin}/auth/callback`;

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isEmailVerified } = useAuth();

  const fromPath = useMemo(() => location.state?.from?.pathname || '/dashboard', [location.state]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && isEmailVerified) {
      navigate(fromPath, { replace: true });
      return;
    }
    if (user && !isEmailVerified) {
      setError('メール認証が必要です。受信トレイの確認メールを開き、リンクをクリックしてください。');
    }
  }, [fromPath, isEmailVerified, navigate, user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!supabase) {
      setError('Supabaseが設定されていません。');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;

      const metadataEmailVerified = data?.user?.user_metadata?.email_verified;
      const emailVerified =
        metadataEmailVerified === undefined || metadataEmailVerified === null
          ? Boolean(data?.user?.email_confirmed_at)
          : Boolean(metadataEmailVerified);

      if (data?.user && !emailVerified) {
        await supabase.auth.signOut();
        setError('メール認証が必要です。確認メールのリンクをクリックしてください。');
        return;
      }
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
        setError('メール認証が必要です。認証メールを再送しました。');
        try {
          await supabase.auth.resend({
            type: 'signup',
            email,
            options: { emailRedirectTo: CALLBACK_URL },
          });
          setMessage('認証メールを送信しました。受信トレイをご確認ください。');
        } catch {
          // ignore
        }
        return;
      }
      if (msg.includes('invalid login credentials')) {
        setError('メールアドレスまたはパスワードが間違っています。');
        return;
      }
      setError('ログインに失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-shape login-shape-1"></div>
        <div className="login-shape login-shape-2"></div>
      </div>

      <div className="login-container">
        <header className="login-header">
          <div className="login-university-name">弘前大学</div>
          <div className="login-app-name">Clinical Clerkship</div>
        </header>

        <main className="login-card">
          <div className="login-welcome-text">ログイン</div>
          <p className="login-welcome-sub">メールアドレスとパスワードを入力してください。</p>

          {error && (
            <div className="login-error-alert" role="alert">
              <AlertCircle size={18} />
              <div>{error}</div>
            </div>
          )}

          {message && (
            <div className="login-page alert is-success" role="status">
              <div className="login-page alert-body">{message}</div>
            </div>
          )}

          <form onSubmit={handleLogin} className="login-form">
            <div className="login-form-group">
              <label htmlFor="email">メールアドレス</label>
              <div className="login-input-wrapper">
                <Mail className="login-input-icon" size={18} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="university@hirosaki-u.ac.jp"
                />
              </div>
            </div>

            <div className="login-form-group">
              <label htmlFor="password">パスワード</label>
              <div className="login-input-wrapper">
                <Lock className="login-input-icon" size={18} />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button type="submit" disabled={submitting} className="login-submit-button">
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} style={{ marginRight: 8 }} />
                  ログイン中...
                </>
              ) : (
                'ログイン'
              )}
            </button>

            <div className="login-links-row">
              <Link to="/forgot-password" className="login-auth-link">
                パスワードをお忘れの方
              </Link>
              <Link to="/register" className="login-auth-link">
                新規ユーザー登録
              </Link>
            </div>
          </form>
        </main>

        <footer className="login-footer">
          &copy; {new Date().getFullYear()} Hirosaki University School of Medicine
        </footer>
      </div>
    </div>
  );
}

export default Login;
